/**
 * Dark palette — used by web-child HomePage and the mobile app shell.
 * One change here propagates to all three clients (web-child, iOS, Android).
 */
export const darkPalette = {
  bg:           '#0D0B1E',
  surface:      '#1A1035',
  surfaceHigh:  '#271564',
  indigo:       '#5B4FE8',
  coral:        '#FF6B50',
  mint:         '#3ECFBD',
  gold:         '#F59E0B',
  reading:      '#10B981',
  writing:      '#AF52DE',
  border:       'rgba(255,255,255,0.10)',
  borderBright: 'rgba(255,255,255,0.22)',
  text:         '#FFFFFF',
  textSub:      'rgba(255,255,255,0.70)',
  textMuted:    'rgba(255,255,255,0.40)',
} as const;

/** Light palette — used by the mobile auth/onboarding forms. */
export const lightPalette = {
  bg:       '#F5F5F7',
  surface:  '#FFFFFF',
  ink:      '#1D1D1F',
  inkSoft:  '#6E6E73',
  inkFaint: '#8E8E93',
  border:   '#E5E5EA',
  accent:   '#007AFF',
  reading:  '#30D158',
  writing:  '#AF52DE',
  warn:     '#FF9500',
  danger:   '#FF3B30',
} as const;

/** Border-radius scale shared across web and mobile. */
export const borderRadius = { sm: 12, md: 16, lg: 20, pill: 999 };

/** CSS pill/card shorthand values used in inline styles. */
export const PILL = '100px';
export const CARD = '20px';

// Design tokens
export const tokens = {
  colors: {
    primary: '#4A90D9',
    secondary: '#7B61FF',
    success: '#34C759',
    warning: '#FF9500',
    error: '#FF3B30',
    background: '#FEFEFE',
    surface: '#F5F5F7',
    text: '#1D1D1F',
    textSecondary: '#6E6E73',
    childPrimary: '#FF6B6B',
    childSecondary: '#4ECDC4',
    childBackground: '#FFF9E6',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '16px',
    xl: '24px',
    full: '9999px',
  },
  fontFamily: {
    default: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    // OpenDyslexic is bundled (ui-shared/src/fonts + @font-face injected by
    // DyslexiaThemeProvider); the rest are per-platform friendly fallbacks
    // ('Chalkboard SE' is the iOS comic-style face — iOS ships no Comic Sans MS).
    dyslexic: "'OpenDyslexic', 'Comic Sans MS', 'Chalkboard SE', 'Comic Neue', sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', monospace",
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
    xxl: '2rem',
    display: '3rem',
  },
} as const;

export type DesignTokens = typeof tokens;

/**
 * High-contrast palette — the TOKEN-LEVEL overrides behind the child
 * "Reading comfort" high-contrast toggle. DyslexiaThemeProvider toggles the
 * `lwa-high-contrast` root class (each app's index.html mirrors these values
 * onto its CSS variables) and exposes them to inline-styled reading surfaces
 * via useTheme().readingSurfaceStyle. Never implement high contrast as a
 * whole-tree CSS filter — that distorts imagery and fails WCAG intent.
 * Keep these values in sync with the `html.lwa-high-contrast` blocks in
 * web-child/index.html, mobile/index.html and web-app/index.html.
 */
export const highContrastTokens = {
  bg: '#000000',
  surface: '#0D0D12',
  surfaceHigh: '#1C1C24',
  text: '#FFFFFF',
  textSub: 'rgba(255,255,255,0.92)',
  textMuted: 'rgba(255,255,255,0.78)',
  border: 'rgba(255,255,255,0.55)',
  accent: '#8FB6FF',
} as const;
